import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import Input, { Textarea } from '../components/common/Input';
import Button from '../components/common/Button';
import { PageLoading } from '../components/common/Loading';
import { ESTADOS_BRASIL } from '../types';
import toast from 'react-hot-toast';

const Perfil: React.FC = () => {
  const { usuario, isLoading, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'perfil' | 'endereco' | 'senha'>('perfil');
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    nome: usuario?.nome || '',
    email: usuario?.email || '',
    telefone: usuario?.telefone || '',
    whatsapp: usuario?.whatsapp || '',
  });

  const [enderecoData, setEnderecoData] = useState({
    cep: usuario?.endereco?.cep || '',
    logradouro: usuario?.endereco?.logradouro || '',
    numero: usuario?.endereco?.numero || '',
    complemento: usuario?.endereco?.complemento || '',
    bairro: usuario?.endereco?.bairro || '',
    cidade: usuario?.endereco?.cidade || '',
    estado: usuario?.endereco?.estado || '',
  });

  const [senhaData, setSenhaData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: '',
  });

  if (isLoading || !usuario) {
    return <PageLoading message="Carregando perfil..." />;
  }

  const handleSavePerfil = async () => {
    setIsSaving(true);
    const success = await updateUser(formData);
    setIsSaving(false);
  };

  const handleSaveEndereco = async () => {
    setIsSaving(true);
    const success = await updateUser({ endereco: enderecoData });
    setIsSaving(false);
  };

  const handleChangePassword = async () => {
    if (senhaData.novaSenha !== senhaData.confirmarSenha) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (senhaData.novaSenha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsSaving(true);
    try {
      // API call would go here
      toast.success('Senha alterada com sucesso');
      setSenhaData({ senhaAtual: '', novaSenha: '', confirmarSenha: '' });
    } catch (error) {
      toast.error('Erro ao alterar senha');
    }
    setIsSaving(false);
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Meu Perfil
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie suas informações pessoais e configurações
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-green-700 to-green-600 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-3xl font-bold">
                {usuario.nome?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{usuario.nome}</h2>
                <p className="text-green-100">{usuario.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    usuario.verificado
                      ? 'bg-green-500 text-white'
                      : 'bg-white/20 text-white'
                  }`}>
                    {usuario.verificado ? '✓ Verificado' : 'Não verificado'}
                  </span>
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium capitalize">
                    {usuario.tipo}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex gap-4 px-6">
              {[
                { id: 'perfil', label: 'Dados Pessoais', icon: '👤' },
                { id: 'endereco', label: 'Endereço', icon: '📍' },
                { id: 'senha', label: 'Segurança', icon: '🔒' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-4 border-b-2 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-green-600 text-green-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Perfil Tab */}
            {activeTab === 'perfil' && (
              <div className="space-y-6 max-w-xl">
                <Input
                  label="Nome completo"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />

                <Input
                  label="E-mail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  helperText="Para alterar o e-mail, entre em contato com o suporte"
                  disabled
                />

                <Input
                  label="Telefone"
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: formatPhone(e.target.value) })}
                  placeholder="(11) 99999-9999"
                />

                <Input
                  label="WhatsApp"
                  type="tel"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: formatPhone(e.target.value) })}
                  placeholder="(11) 99999-9999"
                  helperText="Será exibido nos seus anúncios para contato direto"
                />

                <Button onClick={handleSavePerfil} isLoading={isSaving}>
                  Salvar Alterações
                </Button>
              </div>
            )}

            {/* Endereço Tab */}
            {activeTab === 'endereco' && (
              <div className="space-y-6 max-w-xl">
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="CEP"
                    value={enderecoData.cep}
                    onChange={(e) => {
                      const cep = e.target.value.replace(/\D/g, '');
                      const formatted = cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5, 8)}` : cep;
                      setEnderecoData({ ...enderecoData, cep: formatted });
                    }}
                    placeholder="00000-000"
                    maxLength={9}
                  />
                </div>

                <Input
                  label="Logradouro"
                  value={enderecoData.logradouro}
                  onChange={(e) => setEnderecoData({ ...enderecoData, logradouro: e.target.value })}
                  placeholder="Rua, Avenida, etc."
                />

                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="Número"
                    value={enderecoData.numero}
                    onChange={(e) => setEnderecoData({ ...enderecoData, numero: e.target.value })}
                  />
                  <div className="col-span-2">
                    <Input
                      label="Complemento"
                      value={enderecoData.complemento}
                      onChange={(e) => setEnderecoData({ ...enderecoData, complemento: e.target.value })}
                      placeholder="Apto, Bloco, etc."
                    />
                  </div>
                </div>

                <Input
                  label="Bairro"
                  value={enderecoData.bairro}
                  onChange={(e) => setEnderecoData({ ...enderecoData, bairro: e.target.value })}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Cidade"
                    value={enderecoData.cidade}
                    onChange={(e) => setEnderecoData({ ...enderecoData, cidade: e.target.value })}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Estado
                    </label>
                    <select
                      value={enderecoData.estado}
                      onChange={(e) => setEnderecoData({ ...enderecoData, estado: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                    >
                      <option value="">Selecione</option>
                      {ESTADOS_BRASIL.map((estado) => (
                        <option key={estado.sigla} value={estado.sigla}>
                          {estado.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <Button onClick={handleSaveEndereco} isLoading={isSaving}>
                  Salvar Endereço
                </Button>
              </div>
            )}

            {/* Senha Tab */}
            {activeTab === 'senha' && (
              <div className="space-y-6 max-w-xl">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h4 className="font-semibold text-amber-800 mb-1">
                    🔐 Dicas de segurança
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• Use uma senha com pelo menos 8 caracteres</li>
                    <li>• Combine letras maiúsculas, minúsculas e números</li>
                    <li>• Não use informações pessoais óbvias</li>
                  </ul>
                </div>

                <Input
                  label="Senha atual"
                  type="password"
                  value={senhaData.senhaAtual}
                  onChange={(e) => setSenhaData({ ...senhaData, senhaAtual: e.target.value })}
                  required
                />

                <Input
                  label="Nova senha"
                  type="password"
                  value={senhaData.novaSenha}
                  onChange={(e) => setSenhaData({ ...senhaData, novaSenha: e.target.value })}
                  required
                />

                <Input
                  label="Confirmar nova senha"
                  type="password"
                  value={senhaData.confirmarSenha}
                  onChange={(e) => setSenhaData({ ...senhaData, confirmarSenha: e.target.value })}
                  required
                />

                <Button
                  onClick={handleChangePassword}
                  isLoading={isSaving}
                  disabled={!senhaData.senhaAtual || !senhaData.novaSenha || !senhaData.confirmarSenha}
                >
                  Alterar Senha
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-8 bg-white rounded-2xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-red-600 mb-4">Zona de Perigo</h3>
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
            <div>
              <p className="font-medium text-gray-900">Excluir conta</p>
              <p className="text-sm text-gray-500">
                Uma vez excluída, todos os seus dados serão perdidos permanentemente.
              </p>
            </div>
            <Button variant="danger" size="sm">
              Excluir Conta
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Perfil;
